import { formatDate } from 'utils/date';
import { executeApiCall } from 'services/api';
import { getLoaders } from 'entities/hooks/load';
import { WithUsers } from '../../users';
import { FullInvoice, Invoice, InvoiceCancelationRequest } from '../types';
import { splitDateRange } from './utils';

interface InvoiceByStatusParams {
  status: string;
  startDate?: string;
  endDate?: string;
}

function getInvoices(params: InvoiceByStatusParams): Promise<Invoice[]> {
  return executeApiCall({
    path: '/invoice/invoices',

    method: 'get',

    payload: params,
  });
}

export const invoiceLoaders = getLoaders({
  invoiceCounts: {
    load: async (): Promise<Record<string, number>> => {
      return executeApiCall({
        path: '/invoice/invoices/counts',

        method: 'get',
      });
    },
  },

  findInvoice: {
    load: async ({ id }: { id: string }): Promise<FullInvoice> => {
      return executeApiCall({
        path: `/invoice/invoices/${id}`,

        method: 'get',
      });
    },
  },

  invoicesByStatus: {
    load: async ({
      status,
      endDate,
      startDate,
    }: {
      status: string;
      startDate?: Date;
      endDate?: Date;
    }): Promise<Invoice[]> => {
      if (!startDate || !endDate) return getInvoices({ status });

      const invoices = await Promise.all(
        splitDateRange({ from: startDate, to: endDate }).map(async ({ from, to }) =>
          getInvoices({
            status,
            startDate: formatDate(from, 'yyyy-mm-dd'),
            endDate: formatDate(to, 'yyyy-mm-dd'),
          }),
        ),
      );

      return invoices.flat();
    },
  },

  searchByAccountantId: {
    load: async (payload: { country: string; id: string }): Promise<Invoice[]> => {
      return executeApiCall({
        path: '/invoice/invoices/search-by-accountant-id',

        method: 'get',

        payload,
      });
    },
  },

  activeInvoiceCancelationRequests: {
    load: async (): Promise<WithUsers<InvoiceCancelationRequest, 'requestedBy'>[]> => {
      return executeApiCall({
        path: '/invoice/invoices/cancel/requests',

        method: 'get',
      });
    },
  },
});
